import React, { useEffect, useState, useCallback } from 'react';
import { Grid, Paper, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RootState } from '../../../../reducers';
import { useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import b64toBlob from 'b64-to-blob';
import { useAppDispatch } from '../../../../store/store';
import { useWidgetContext } from '../../../../hooks/widgetContext';
import { AppointmentTimeFilterEnum } from '../../../../Enum/AppointmentTimeFilterEnum';
import { fetchMembersNotScheduledWithBHOrNP, exportMembersNotScheduledWithBHOrNP } from '../../../Widgets/WidgetService';
import { parentUrl } from '../../../../utils/constants';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { generateRandomId, isDefaultDate } from '../../../../utils/common';
import { dateFormatter } from '../../../../utils/timeFormat';
import TeamDropdown from '../../../Shared/TeamDropdown';
import { fetchTeams } from '../../../../store/careTeams.slice';

const MembersNotScheduledWithBHorNPDetail = () => {
    const dispatch = useAppDispatch();
    const timePeriods = [
        { label: AppointmentTimeFilterEnum.Next30DaysDescription, value: AppointmentTimeFilterEnum.Next30Days },
        { label: AppointmentTimeFilterEnum.Next60DaysDescription, value: AppointmentTimeFilterEnum.Next60Days },
        { label: AppointmentTimeFilterEnum.Next90DaysDescription, value: AppointmentTimeFilterEnum.Next90Days },
        { label: AppointmentTimeFilterEnum.CustomDescription, value: AppointmentTimeFilterEnum.Custom },
    ];
    const history = useNavigate();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [isMembersLoading, setIsMembersLoading] = useState<boolean>(false);
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [membersNotScheduled, setMembersNotScheduled] = useState([]);
    const {
        scheduledFilterBy,
        setScheduledFilterBy,
        scheduledSelectedTeam,
        setScheduledSelectedTeam,
        scheduledShowInActiveTeams,
        setScheduledShowInActiveTeams,
        scheduledFromDate,
        setScheduledFromDate,
        scheduledToDate,
        setScheduledToDate,
    } = useWidgetContext();

    const initialPage = useCallback(async () => {
        dispatch(fetchTeams(true));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        const changeFilter = async () => {
            try {
                setIsMembersLoading(true);
                setMembersNotScheduled([]);
                let dateFrom = Boolean(scheduledFromDate) && scheduledFilterBy === AppointmentTimeFilterEnum.Custom ? new Date(scheduledFromDate).toISOString() : '';
                let dateTo = Boolean(scheduledToDate) && scheduledFilterBy === AppointmentTimeFilterEnum.Custom ? new Date(scheduledToDate).toISOString() : '';
                const response = await fetchMembersNotScheduledWithBHOrNP(
                    scheduledSelectedTeam,
                    scheduledFilterBy,
                    dateFrom,
                    dateTo,
                    scheduledShowInActiveTeams,
                );
                setMembersNotScheduled(response);
                setIsMembersLoading(false);
            } catch (e) {
                setMembersNotScheduled([]);
                setIsMembersLoading(false);
                console.log(e);
            }
        };

        if (scheduledFilterBy !== AppointmentTimeFilterEnum.Custom || (Boolean(scheduledFromDate) && Boolean(scheduledToDate))) {
            changeFilter();
        }

        if (scheduledFilterBy !== AppointmentTimeFilterEnum.Custom && Boolean(scheduledFromDate) && Boolean(scheduledToDate)) {
            setScheduledFromDate(null);
            setScheduledToDate(null);
        }
    }, [scheduledFilterBy, scheduledSelectedTeam, scheduledShowInActiveTeams, scheduledFromDate, scheduledToDate]);

    const backToWidget = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    const exportDetail = async () => {
        setDownLoading(true);
        let dateFrom = Boolean(scheduledFromDate) && scheduledFilterBy === AppointmentTimeFilterEnum.Custom ? new Date(scheduledFromDate).toISOString() : '';
        let dateTo = Boolean(scheduledToDate) && scheduledFilterBy === AppointmentTimeFilterEnum.Custom ? new Date(scheduledToDate).toISOString() : '';
        const data = await exportMembersNotScheduledWithBHOrNP(
            scheduledSelectedTeam,
            scheduledFilterBy,
            dateFrom,
            dateTo,
            scheduledShowInActiveTeams,
        );
        let blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `MemberNoScheduledWithBHorNP - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    const columns: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 60,
            sortable: false,
            renderCell: (params) => {
                return <Typography>{params.api.getSortedRowIds().indexOf(params.id) + 1}</Typography>;
            },
        },
        {
            field: 'AhcccsId',
            headerName: 'AHCCCS Id',
            flex: 1,
        },
        {
            field: 'FirstName',
            headerName: 'First Name',
            flex: 1,
        },
        {
            field: 'LastName',
            headerName: 'Last Name',
            flex: 1,
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            flex: 0.8,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.DOB) ? '-' : dateFormatter(new Date(params.row.DOB))}</>;
            },
        },
        {
            field: 'Population',
            headerName: 'Population',
            flex: 1,
        },
        {
            field: 'HealthPlan',
            headerName: 'Health Plan',
            flex: 1,
        },
        {
            field: 'Status',
            headerName: 'Status',
            flex: 1,
        },
        {
            field: 'DateOfOnboard',
            headerName: 'Date Of Onboard',
            flex: 1.2,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.DateOfOnboard) ? '-' : dateFormatter(new Date(params.row.DateOfOnboard))}</>;
            },
        },
        {
            field: 'LastNPVisit',
            headerName: 'Last In-Person Visit With Licensed Provider',
            flex: 1.2,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.LastNPVisit) ? '-' : dateFormatter(new Date(params.row.LastNPVisit))}</>;
            },
        },
        {
            field: 'LastVisit',
            headerName: 'Last In-Person Visit With Any Provider',
            flex: 1.2,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.LastVisit) ? '-' : dateFormatter(new Date(params.row.LastVisit))}</>;
            },
        },
        {
            field: 'PrimaryProvider',
            headerName: 'Primary Provider',
            flex: 1,
        },
        {
            field: 'IsContacted',
            headerName: 'Contacted In Last Engagement',
            flex: 1.2,
            renderCell: (params) => {
                return <>{params.row.IsContacted ? 'Yes' : 'No'}</>;
            },
        },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={5}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                    <Grid size={12} paddingBottom={1}>
                        <span onClick={backToWidget}>
                            <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                        </span>
                        <Typography display={'inline-block'} variant="h6" color={'primary'}>
                            Members Not Scheduled With BH or NP ({membersNotScheduled?.length})
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container pl={2} mt={0} spacing={2}>
                    <Grid size={2}>
                        <TeamDropdown
                            teams={teams}
                            selectedTeam={scheduledSelectedTeam}
                            onChange={(id) => setScheduledSelectedTeam(id)}
                            showInactive={scheduledShowInActiveTeams}
                            onToggleInactive={setScheduledShowInActiveTeams}
                        />
                    </Grid>
                    <Grid size={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Date Filter</InputLabel>
                            <Select
                                value={scheduledFilterBy}
                                label="Date Filter"
                                name="Date"
                                onChange={(e) => setScheduledFilterBy(parseInt(e.target.value as any))}
                            >
                                {timePeriods.map((date) => (
                                    <MenuItem key={date.value} value={date.value}>
                                        {date.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {scheduledFilterBy === AppointmentTimeFilterEnum.Custom ? (
                        <React.Fragment>
                            <Grid size={{ md: 1.5 }}>
                                <DesktopDatePicker
                                    label="From Date"
                                    value={Boolean(scheduledFromDate) ? scheduledFromDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => setScheduledFromDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                            <Grid size={{ md: 1.5 }}>
                                <DesktopDatePicker
                                    label="To Date"
                                    value={Boolean(scheduledToDate) ? scheduledToDate : null}
                                    minDate={new Date('2017-01-01')}
                                    onChange={(newValue) => setScheduledToDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </Grid>
                            <Grid size={3.8}></Grid>
                        </React.Fragment>
                    ) : (
                        <Grid size={6.8}></Grid>
                    )}
                    <Grid size={1}>
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto' }}
                            onClick={() => exportDetail()}
                            variant="contained"
                            color="success"
                            disabled={!membersNotScheduled.length}
                        >
                            {downLoading ? 'Exporting...' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <hr />
                <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingBottom={2}>
                    <Grid size={12} minHeight="75vh" sx={{ position: 'relative' }}>
                        {isMembersLoading ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                <>
                                    <Grid size={12} style={{ height: '75vh', width: '100%' }}>
                                        <DataGrid
                                            columns={columns}
                                            rows={membersNotScheduled}
                                            getRowId={(row) => `${row.Id}-${generateRandomId()}`}
                                            paginationMode="server"
                                            hideFooterPagination={true}
                                            hideFooter={true}
                                            onCellClick={(params: any) => {
                                                window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                            }}
                                        />
                                    </Grid>
                                </>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </LocalizationProvider>
    );
};

export default MembersNotScheduledWithBHorNPDetail;